import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toastError } from '../../../shared/toastHelper';

// O-5024: ONE implementation of the event-template picker's list, shared by
// every editor shell.
//
// There were three near-copies of this fetch, and the two shells had already
// drifted apart on what happens after a save: the mobile one upserted, while the
// desktop controller kept a separate append for "created" and a map for "saved"
// — and that map silently dropped a save for a template not already in its list,
// which is exactly what a save-as-new on one shell then saving from the other
// produces. Upserting is the behaviour that is correct for both.
//
// Only V3-backed templates: one carrying just the legacy V2 finance items has
// nothing to give a V3 offer or budget.
const toEntry = (template) => ({
  value: template.id,
  label: template.name,
  offerTemplateId: template.v3_offer_template_id,
});

export default function useEventTemplates(teamId) {
  const [eventTemplates, setEventTemplates] = useState([]);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return undefined;
    fetched.current = true;

    let ignore = false;
    axios.get(`/teams/${teamId}/event_templates.json`, { params: { v3_backed: true } })
      .then((res) => { if (!ignore) setEventTemplates((res.data || []).map(toEntry)); })
      // Failing silently leaves an empty picker, which reads as "you have no
      // event templates" — a different and wrong answer to the one asked.
      .catch(() => { if (!ignore) toastError('Failed to load event templates. Please try again.'); });
    return () => { ignore = true; };
  }, [teamId]);

  // Covers both "just created" and "just saved": a save-to-current can give a
  // template its first offer template, and a save-as-new produces one the list
  // has never seen.
  const upsertEventTemplate = useCallback((saved) => {
    if (!saved?.id) return;
    setEventTemplates((prev) => {
      const entry = toEntry(saved);
      return prev.some((t) => t.value === entry.value)
        ? prev.map((t) => (t.value === entry.value ? { ...t, ...entry } : t))
        : [...prev, entry];
    });
  }, []);

  return { eventTemplates, upsertEventTemplate };
}
